// @ts-strict-ignore
import {
  Component,
  effect,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgClass } from '@angular/common';
import {
  defaultNavigation,
  filterNavigation,
} from '@fc-core/navbar/navigation/navigation.data';
import { IconComponent } from '@fc-shared/ui/icon/icon.component';
import { NavigationItemComponent } from '@fc-core/navbar/navigation/navigation-item/navigation-item.component';
import { NavigationItem } from '@fc-core/navbar/navigation/navigation-item';
import { DialogsService } from '@fc-core/services';
import { getCurrentEmployeePermissions } from '@fc-store/selectors/current-user.selector';
import { Store } from '@ngrx/store';
import { FeatureFlagService } from '@fc-core/feature-flags/feature-flag.service';
import { getShowNews } from '@fc-store/selectors/news.selectors';
import { cloneDeep } from 'lodash-es';
import { ChatFacadeService } from '@fc-chats/store/chat-facade.service';
import { getCurrentCustomer } from '@fc-store/selectors/current-customer.selector';
import { getNavbarState } from '@fc-store/selectors/ui.selector';
import { setNavbarState } from '@fc-store/actions';
import { UserCustomerSelectComponent } from 'src/app/dialogs/customer-chooser-dialog/user-customer-select.component';
import { AmplitudeService } from '@fc-core/services/amplitude.service';
import { ChatChannelStore } from '@fc-chats/store/chat-channel-store';

@Component({
  selector: 'fc-navigation',
  imports: [IconComponent, RouterModule, NavigationItemComponent, NgClass],
  host: { class: 'h-full flex-shrink-0' },
  template: `
    <div
      class="fixed xl:hidden inset-y-0 left-0 z-50 flex flex-col h-full overflow-hidden
             transition-all duration-300 ease-in-out bg-primary-1000
             w-280 md:w-480 xl:w-240 -translate-x-full"
      [ngClass]="{
        'translate-x-0': navbarState(),
        '-translate-x-full': !navbarState(),
      }"
    >
      <div
        class="flex gap-12 py-16 px-20 items-center border-b border-primary-950 overflow-hidden"
      >
        <div
          class="flex items-center gap-12 mr-auto whitespace-nowrap"
          [class.hidden]="!navbarState()"
        >
          <fc-icon icon="icon-business" color="white" [size]="24" />
          <div class="text-sm text-white max-w-140 truncate">
            {{ currentCustomer()?.name }}
          </div>
        </div>

        <fc-icon
          icon="icon-close"
          color="white"
          [size]="20"
          class="cursor-pointer"
          (click)="closeNav()"
        />
      </div>

      <div class="navigation-scroll scrollable flex flex-col flex-1 min-h-0">
        <div class="flex flex-col gap-12 py-20 px-8">
          <fc-navigation-item
            [link]="{
              name: 'Search',
              icon: 'icon-search-outline',
              iconActive: 'icon-search-bold',
              link: '/search',
            }"
            [isCollapsed]="false"
            (toggle)="toggleMenu($event)"
            (onClick)="closeNav()"
          />
          @for (link of defaultNavigationItems; track $index) {
            <fc-navigation-item
              [link]="link"
              [isCollapsed]="false"
              (toggle)="toggleMenu($event)"
              (onClick)="closeNav()"
            />
          }
        </div>
      </div>

      <div class="flex flex-col gap-12 py-8 px-8 border-t border-primary-950">
        <fc-navigation-item
          [link]="settingsItem"
          [isCollapsed]="false"
          (onClick)="closeNav()"
        />
        <fc-navigation-item
          [link]="switchCustomerItem"
          [isCollapsed]="false"
          (onClick)="openCustomerChooserDialog()"
        />
      </div>
    </div>

    <div
      class="fixed inset-0 bg-black-blur-xs z-40 xl:hidden"
      [class.hidden]="!navbarState()"
      (click)="closeNav()"
    ></div>

    <div
      class="hidden xl:flex flex-col h-full overflow-hidden transition-all duration-300 ease-in-out bg-primary-1000 z-50"
      [ngClass]="{
        'w-64': isCollapsed(),
        'w-280 md:w-480 xl:w-240': !isCollapsed(),
      }"
    >
      <div
        class="flex gap-12 py-16 px-20 items-center border-b border-primary-950 overflow-hidden"
      >
        <div
          animate.enter="fade-enter"
          animate.leave="fade-leave"
          class="flex items-center gap-12 mr-auto whitespace-nowrap"
          [class.hidden]="isCollapsed()"
        >
          <fc-icon icon="icon-business" color="white" [size]="24" />
          <div class="text-sm text-white max-w-140 truncate">
            {{ currentCustomer()?.name }}
          </div>
        </div>

        <fc-icon
          [icon]="
            !isCollapsed()
              ? 'icon-sidebar-left-outline'
              : 'icon-sidebar-right-outline'
          "
          color="white"
          [size]="24"
          class="cursor-pointer"
          (click)="toggleCollapsed()"
        />
      </div>

      <div class="navigation-scroll scrollable flex flex-col flex-1 min-h-0">
        <div class="flex flex-col gap-12 py-20 px-8">
          <fc-navigation-item
            [link]="{
              name: 'Search',
              icon: 'icon-search-outline',
              iconActive: 'icon-search-bold',
              link: '/search',
            }"
            [isCollapsed]="isCollapsed()"
            (onClick)="closeNav()"
          />
          @for (link of defaultNavigationItems; track $index) {
            <fc-navigation-item
              [link]="link"
              [isCollapsed]="isCollapsed()"
              (toggle)="toggleMenu($event)"
            />
          }
        </div>
      </div>

      <div class="flex flex-col gap-12 py-8 px-8 border-t border-primary-950">
        <fc-navigation-item
          [link]="settingsItem"
          [isCollapsed]="isCollapsed()"
        />
        <fc-navigation-item
          [link]="switchCustomerItem"
          [isCollapsed]="isCollapsed()"
          (onClick)="openCustomerChooserDialog()"
        />
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    @layer navbar-animations {
      .fade-enter {
        animation: fadeEnter 0.25s ease-out 0.05s both;
      }
      .fade-leave {
        animation: fadeLeave 0.2s ease-in both;
      }
      @keyframes fadeEnter {
        from {
          opacity: 0;
          max-width: 0;
          overflow: hidden;
        }
        to {
          opacity: 1;
          max-width: 300px;
          overflow: hidden;
        }
      }
      @keyframes fadeLeave {
        from {
          opacity: 1;
          max-width: 300px;
          overflow: hidden;
        }
        to {
          opacity: 0;
          max-width: 0;
          overflow: hidden;
        }
      }
    }

    :host {
      min-height: 0;
    }

    /*
      The global custom scrollbar is a classic (space-taking) one: in the 64px
      collapsed rail it steals width, pushing icons off-center and shrinking
      the hover/active pills. Hide it here — trackpad/wheel/keyboard scrolling
      still works, matching the macOS overlay-scrollbar feel.
    */
    .navigation-scroll {
      scrollbar-width: none;
    }

    /* Fallback for browsers without scrollbar-width (Safari < 18.2) */
    .navigation-scroll::-webkit-scrollbar {
      display: none;
    }
  `,
})
export class NavigationComponent {
  store = inject(Store);
  amplitude = inject(AmplitudeService);
  chatFeature = inject(ChatFacadeService);
  dialogsService = inject(DialogsService);
  chatStore = inject(ChatChannelStore);
  navbarState = this.store.selectSignal(getNavbarState);
  newsToShowState = this.store.selectSignal(getShowNews);
  features = inject(FeatureFlagService).effectiveFeatures;
  currentPermissions = this.store.selectSignal(getCurrentEmployeePermissions);
  currentCustomer = this.store.selectSignal(getCurrentCustomer);

  openMenus: Record<string, boolean> = {};
  defaultNavigationItems: NavigationItem[] = this.getNavigationList();

  readonly isCollapsed = signal(true);

  constructor() {
    this.chatStore.getChannels();
    effect(() => (this.defaultNavigationItems = this.getNavigationList()));
  }

  getNavigationList(): NavigationItem[] {
    const newsToShow = this.newsToShowState(),
      permissions = this.currentPermissions(),
      features = this.features(),
      messages = this.chatFeature.getAllUnreadMessages(),
      navigation = cloneDeep(defaultNavigation);

    if (permissions !== null && features !== null) {
      const filtered = filterNavigation(
        navigation,
        permissions,
        features,
        newsToShow,
        messages,
      );
      filtered.forEach((item) => {
        item.open = this.openMenus[item?.name] || false;
      });
      return filtered;
    }
    return [];
  }

  settingsItem: NavigationItem = {
    name: 'Settings',
    icon: 'icon-settings-outline',
    iconActive: 'icon-settings-bold',
    link: '/settings',
  };

  switchCustomerItem: NavigationItem = {
    name: 'Switch Company',
    icon: 'icon-swap-outline',
    iconActive: 'icon-swap-bold',
  };

  toggleMenu(link: NavigationItem) {
    this.openMenus[link.name] = !(this.openMenus[link.name] || false);
    Object.keys(this.openMenus).forEach((key) => {
      if (key !== link.name) this.openMenus[key] = false;
    });
    this.defaultNavigationItems = this.getNavigationList();
  }

  toggleCollapsed() {
    this.isCollapsed.set(!this.isCollapsed());
  }

  closeNav() {
    this.store.dispatch(setNavbarState({ state: false }));
  }

  openCustomerChooserDialog() {
    this.amplitude.track('nav_click_switch_company');
    this.closeNav();
    this.dialogsService.openFullScreenDialog(UserCustomerSelectComponent);
  }
}
